import PropTypes from 'prop-types';
import React from 'react';
import DropZone from 'react-dropzone';
import Bytes from 'bytes';
import ClassNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import LinearProgress from '@material-ui/core/LinearProgress';
import Button from '@material-ui/core/Button';
import DialogBox from './dialog-box';
import { fetch, isNetworkError } from '../services/fetch';
import bugsnagClient from '../services/bugsnag';

class DropZoneWrapper extends React.PureComponent {

  state = {
    dialogOpen : false,
    uploadingFiles: false,
    uploadError: null,
   };

  onDragEnter() {
    return new Promise((resolve) => this.setState({
      draggingOverZone: true
    }, resolve));
  }

  onDragExit() {
    return new Promise((resolve) => this.setState({
      draggingOverZone: false
    }, resolve));
  }

  async uploadFile(file, folder) {
    if (this.props.onFileBeforeUpload instanceof Function) {
      const base64Reader = await new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onloadend = resolve.bind(null, reader);
        reader.onerror = reject;
        reader.onabort = reject;

        reader.readAsDataURL(file);
      });
      await this.props.onFileBeforeUpload(file, base64Reader.result);
    }

    const formData = new FormData();

    formData.append('file', file);

    const response = await fetch('/upload', {
      method: 'POST',
      body: formData,
      retries: 2
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }

    const details = Object.assign({}, await response.json(), {folder});

    return this.props.onFileDetails(details, file);
  }

  // Uploads one at a time. Each file is removed from state once it's uploaded,
  // so if one fails, retrying only sends the files that are left
  async onFileDropContinued(files, folder = ''){
    const filesLimit = this.props.fileLimit ? files.slice(0, this.props.fileLimit) : files;

    await new Promise((resolve) => this.setState({
      uploadingFiles: true,
      uploadError: null
    }, resolve));

    try {
      for (const file of filesLimit) {
        await this.uploadFile(file, folder);
        await new Promise((resolve) => this.setState(({ files: remaining }) => ({
          files: (remaining || []).filter(item => item !== file)
        }), resolve));
      }
    } finally {
      await new Promise((resolve) => this.setState({
        uploadingFiles: false
      }, resolve));
    }
  }

  async handleUpload(folder){
    try {
      await this.onFileDropContinued(this.state.files, folder);
    } catch (e) {
      if (!isNetworkError(e)) {
        bugsnagClient.notify(e);
      }
      // Keep the dialog open with the remaining files so the user can retry
      this.setState({
        uploadError: isNetworkError(e)
          ? 'Upload failed. Check your connection and choose a folder to try again.'
          : 'Upload failed. Choose a folder to try again.'
      });
      return;
    }
    this.setState({
      files: null,
      dialogOpen: false
    });
  }

  async onFileDrop(files) {
    await this.onDragExit();
    this.setState({
      dialogOpen: true,
      files: files,
      uploadError: null
    })
  }

  handleClose = () =>{
    this.setState({
      dialogOpen: false
    });
  };

  render() {
    const dropzoneWrapperClass = ClassNames('dropzone-wrapper flex-grid row', this.state.draggingOverZone ? 'dropzone-drag' : '', this.props.className || ''),
      dropzoneStyles = {
        width: '100%'
      },
      maxUpload = 'number' === typeof this.props.maxUpload && !isNaN(this.props.maxUpload) && this.props.maxUpload || Bytes('64mb');

    let controls = (
      <div
        className="dropzone-contents"
        style={dropzoneStyles}
        onDragEnter={this.onDragEnter.bind(this)}
        onDragLeave={this.onDragExit.bind(this)}
        onDragEnd={this.onDragExit.bind(this)}
        onDragExit={this.onDragExit.bind(this)}
      >
        <LinearProgress
          mode="indeterminate"
          className={this.state.uploadingFiles || this.props.showProgress ? 'uploading-files' : 'not-uploading-files'}
        />
        <div className="flex-grid flex-col-center">
          <h3><FormattedMessage id="app.dropZone.uploadMessage" /></h3>
          <span><FormattedMessage id="app.dropZone.uploadOrMessage" /></span>
          <Button
            variant="contained"
            className="select-file-button"
          >
            <FormattedMessage id="app.dropZone.uploadSelectMessage" />
          </Button>
          <span><FormattedMessage id="app.dropZone.maxUpload" values={{ bytes: Bytes(maxUpload) }} /></span>
        </div>
        {this.props.children}
      </div>
    );

    if (this.props.controls) {
      controls = this.props.controls;
    }
    const actions = [
      <Button
        variant="contained"
        style={{marginRight: '20px'}}
        default={true}
        onClick={this.handleClose}
      >
        Cancel
      </Button>
    ];

    return (
      <div className={dropzoneWrapperClass}>
        <DropZone
          onDrop={this.onFileDrop.bind(this)}
          className="flex flex-grid row center"
          maxSize={maxUpload}
          accept={this.props.accept}
        >
          {controls}
        </DropZone>
        <DialogBox
          showProgress={this.state.uploadingFiles || this.props.showProgress}
          handleUpload = {this.handleUpload.bind(this)}
          uploadingFiles = {this.state.uploadingFiles}
          error = {this.state.uploadError}
          handleClose = {this.handleClose}
          dialogOpen = {this.state.dialogOpen}
          actions = {actions}
        />
      </div>
    );
  }
}

DropZoneWrapper.propTypes = {
  onFileBeforeUpload: PropTypes.func,
  onFileDetails: PropTypes.func.isRequired,
  fileLimit: PropTypes.number,
  accept: PropTypes.string,
  maxUpload: PropTypes.number,
  children: PropTypes.any,
  controls: PropTypes.any,
  className: PropTypes.string,
  showProgress: PropTypes.bool
};

export default DropZoneWrapper;
